import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
// import { CourierDto, CourierResolverDto, CouriersApi, ResolversApi } from '../shared/sdk';
import { apiConfig } from '../shared/api-config';
import {
  CourierApi,
  CourierResolverApi,
  PartnerLinkApi,
  CourierResolver,
  Courier,
  StoreLocation,
} from '@shared/sdk-v1';
import { PayloadAction } from '@reduxjs/toolkit';
import { CourierSettings } from '@shared/sdk-v1';
import { ThemeSettingApi } from '@shared/sdk-v1';
import { ThemeSetting } from '@shared/sdk-v1';
import { RootState } from '../store';
import { injectGtm } from '../utils/injectGtm';

export const fetchCourier = createAsyncThunk(
  'courier/fetchCourier',
  async () => {
    const resolverApi = new CourierResolverApi(apiConfig());
    const courierApi = new CourierApi(apiConfig());

    const host = window.location.host;
    const resolverRecord: CourierResolver =
      await resolverApi.courierResolverFindOne({
        filter: JSON.stringify({
          where: {
            or: [{ customerUrl: host }],
          },
          fields: {
            courierId: true,
          },
        }),
      });
    // console.log(`Resolved to courier id ${resolverRecord?.courierId}`);

    if (!resolverRecord?.courierId) {
      throw new Error('No courier found');
    }

    const courier = await courierApi.courierFindById({
      id: resolverRecord?.courierId?.toString(),
      filter: JSON.stringify({
        fields: {
          id: true,
          isActive: true,
          realm: true,
          name: true,
          slogan: true,
          imageUrl: true,
          mailboxPaddingLength: true,
          email: true,
          webUrl: true,
        },
        include: [
          'storeLocations',
          'themeSetting',
          {
            relation: 'courierSetting',
            scope: {
              fields: {
                prefix: true,
                isJaShippersApiEnabled: true,
                jaShippersApiKey: true,
                isDeliveryEnabled: true,
                deliveryApiKey: true,
                isLogisApiEnabled: true,
                logisApiAccessToken: true,
                logisApiBaseUrl: true,
                processingFee1: true,
                processingFee2: true,
                processingFee3: true,
                processingFee4: true,
                processingFeeWeightLimit: true,
                weightPrice: true,
                expressWeightPrice: true,
                isFygaroApiEnabled: true,
                whatsappBotEnabled: true,
                statusAlertOptions: true,
                additionalShippingAddresses: true,
                whatsappServerUrl: true,
                isShipCorpApiEnabled: true,
                shipCorpApiAccessToken: true,
                shipCorpApiBaseUrl: true,
                isScanInEnabled: true,
                landingPageVersion: true,
                isShipBizApiEnabled: true,
                shipBizApiKey: true,
                shipBizApiBaseUrl: true,
                useWaApi: true,
                isSkgApiEnabled: true,
                requireVerification: true,
                requireTrnOnSignup: true,
                requireAddressOnSignup: true,
                isReferredByEnabled: true,
                isReferralSourceEnabled: true,
                referralSourceOptions: true,
                isPreAlertSupported: true,
                barrelPrice: true,
                pricePerCubicFoot: true,
                insuranceFee: true,
                seaShippingEnabled: true,
                contactInformation: true,
                storeAddress: true,
                fixedCustomRate: true,
                useJmd: true,
                currency: true,
                showDualCurrency: true,
                exchangeRate: true,
                isSmsApiEnabled: true,
                isPasskeyEnabled: true,
                useHalfPound: true,
                halfPoundPrice: true,
                weightRoundingMode: true,
                isWarehouseApiEnabled: true,
                turnstileSiteKey: true,
                isShopForMeEnabled: true,
                isShopEnabled: true,
                isLocalDeliveryEnabled: true,
                isCustomerChatEnabled: true,
                isReviewsEnabled: true,
                isGalleryEnabled: true,
                isCustomsCalculatorEnabled: true,
                gtmContainerId: true,
              },
            },
          },
          {
            relation: 'courierBills',
            scope: {
              fields: {
                id: true,
                status: true,
                dateDue: true,
              },
              where: {
                status: 'Unpaid',
              },
            },
          },
          {
            relation: 'wipayCredentials',
            scope: {
              fields: {
                isWipayApiEnabled: true,
                wipayApiKey: true,
                wipayAccountNumber: true,
                wipayCountryCode: true,
                wipayCurrency: true,
                wipayApiBaseUrl: true,
                wipayFeeStructure: true,
                wipayEnvironment: true,
                wipayResponseUrl: true,
              },
            },
          },
          {
            relation: 'stripeCredentials',
            scope: {
              fields: {
                isStripeApiEnabled: true,
                restrictedKey: true,
              },
            },
          },
          {
            relation: 'rewardSetting',
            scope: {
              fields: {
                packagePoints: true,
                recruitedPoints: true,
                shippedPoints: true,
                phoneVerifiedPoints: true,
              },
            },
          },
          {
            relation: 'storeLocations',
          },
          {
            relation: 'legalDocument',
            scope: {
              fields: {
                termsOfService: true,
                privacyPolicy: true,
                lastUpdatedTerms: true,
                lastUpdatedPrivacy: true,
              },
            },
          }
        ],
      }),
    });
    // console.log('courier', courier);

    return courier;
  }
);

export const fetchCourierStoreLocations = createAsyncThunk(
  'courier/fetchStoreLocations',
  async (courierId: string) => {
    const courierApi = new CourierApi(apiConfig());
    const response = await courierApi.courierPrototypeGetStoreLocations({
      id: courierId,
    });
    return response;
  }
);

export const fetchCourierThemeSettings = createAsyncThunk(
  'courier/fetchThemeSettings',
  async (courierId: string) => {
    const themeSettingApi = new ThemeSettingApi(apiConfig());
    const filter = JSON.stringify({ where: { courierId: Number(courierId) } });
    const response = await themeSettingApi.themeSettingFind({ filter });
    return response[0] || null;
  }
);

export const fetchCourierForImpersonation = createAsyncThunk(
  'courier/fetchCourierForImpersonation',
  async (courierId: string) => {
    const courierApi = new CourierApi(apiConfig());

    const courier = await courierApi.courierFindById({
      id: courierId,
      filter: JSON.stringify({
        fields: {
          id: true,
          isActive: true,
          realm: true,
          name: true,
          imageUrl: true,
          mailboxPaddingLength: true,
          email: true,
          webUrl: true,
        },
        include: [
          'storeLocations',
          'themeSetting',
          {
            relation: 'courierSetting',
            scope: {
              fields: {
                prefix: true,
                isJaShippersApiEnabled: true,
                jaShippersApiKey: true,
                isDeliveryEnabled: true,
                deliveryApiKey: true,
                isLogisApiEnabled: true,
                logisApiAccessToken: true,
                logisApiBaseUrl: true,
                processingFee1: true,
                processingFee2: true,
                processingFee3: true,
                processingFee4: true,
                processingFeeWeightLimit: true,
                weightPrice: true,
                expressWeightPrice: true,
                isFygaroApiEnabled: true,
                whatsappBotEnabled: true,
                statusAlertOptions: true,
                additionalShippingAddresses: true,
                whatsappServerUrl: true,
                isShipCorpApiEnabled: true,
                shipCorpApiAccessToken: true,
                shipCorpApiBaseUrl: true,
                isScanInEnabled: true,
                landingPageVersion: true,
                isShipBizApiEnabled: true,
                shipBizApiKey: true,
                shipBizApiBaseUrl: true,
                useWaApi: true,
                isSkgApiEnabled: true,
                isSmsApiEnabled: true,
                isPasskeyEnabled: true,
                weightRoundingMode: true,
                isWarehouseApiEnabled: true,
                isShopForMeEnabled: true,
                isShopEnabled: true,
                isLocalDeliveryEnabled: true,
                isCustomerChatEnabled: true,
                isReviewsEnabled: true,
                isGalleryEnabled: true,
                isCustomsCalculatorEnabled: true,
                gtmContainerId: true,
              },
            },
          },
          {
            relation: 'courierBills',
            scope: {
              fields: {
                id: true,
                status: true,
                dateDue: true,
              },
              where: {
                status: 'Unpaid',
              },
            },
          },
          {
            relation: 'wipayCredentials',
            scope: {
              fields: {
                isWipayApiEnabled: true,
                wipayApiKey: true,
                wipayAccountNumber: true,
                wipayCountryCode: true,
                wipayCurrency: true,
                wipayApiBaseUrl: true,
                wipayFeeStructure: true,
                wipayEnvironment: true,
                wipayResponseUrl: true,
              },
            },
          },
          {
            relation: 'stripeCredentials',
            scope: {
              fields: {
                isStripeApiEnabled: true,
                restrictedKey: true,
              },
            },
          },
        ],
      }),
    });

    return courier;
  }
);

export const checkPartnerLinked = createAsyncThunk(
  'courier/checkPartnerLinked',
  async (courierId: string) => {
    const partnerLinkApi = new PartnerLinkApi(apiConfig());
    const result = await partnerLinkApi.partnerLinkIsPartnerLinked({
      courierId,
    });
    return result.isPartnerLinked;
  }
);

interface CourierState {
  isLoading: boolean;
  courier?: Courier;
  isBillDue?: boolean;
  error?: string;
  storeLocations: StoreLocation[];
  isPartnerLinked: boolean;
  // Impersonation state
  isImpersonating: boolean;
  originalCourier?: Courier;
  impersonatedCourier?: Courier;
}

const initialState: CourierState = {
  isLoading: true,
  courier: undefined,
  error: undefined,
  storeLocations: [],
  isPartnerLinked: false,
  // Impersonation initial state
  isImpersonating: false,
  originalCourier: undefined,
  impersonatedCourier: undefined,
};

export const courierSlice = createSlice({
  name: 'courier',
  initialState,
  reducers: {
    updateCourier: (state, action) => {
      state.courier = action.payload;
      state.error = undefined;
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
      state.error = undefined;
    },
    updateCourierSettings: (state, action: PayloadAction<CourierSettings>) => {
      if (state.courier) {
        state.courier.courierSetting = action.payload;
      }
    },
    updateStoreLocations: (state, action: PayloadAction<StoreLocation[]>) => {
      state.storeLocations = action.payload;
    },
    updateThemeSettings: (state, action: PayloadAction<ThemeSetting>) => {
      if (state.courier) {
        state.courier.themeSetting = action.payload;
      }
    },
    // Impersonation Actions
    startImpersonation: (state, action: PayloadAction<Courier>) => {
      // Only allow impersonation if not already impersonating and we have a current courier
      if (!state.isImpersonating && state.courier) {
        // Store the original courier for restoration later
        state.originalCourier = state.courier;
        state.impersonatedCourier = action.payload;
        state.isImpersonating = true;
        // Set the main courier to the impersonated one for seamless integration
        // This ensures all existing code that reads state.courier works correctly
        state.courier = action.payload;
      }
    },
    stopImpersonation: (state) => {
      // Only allow stopping if currently impersonating and we have an original courier
      if (state.isImpersonating && state.originalCourier) {
        // Restore the original courier
        state.courier = state.originalCourier;
        // Clear impersonation state
        state.isImpersonating = false;
        state.originalCourier = undefined;
        state.impersonatedCourier = undefined;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCourier.pending, (state) => {
      state.courier = undefined;
      state.isLoading = true;
      state.error = undefined;
    });
    builder.addCase(fetchCourier.fulfilled, (state, action) => {
      // console.log('action.payload', action.payload);
      // During impersonation, preserve the impersonated courier state
      // and store the fetched courier as the original courier if not already set
      if (state.isImpersonating) {
        // If we don't have an original courier stored, store the fetched one
        if (!state.originalCourier) {
          state.originalCourier = action.payload;
        }
        // Don't update the main courier state during impersonation
        // The effective courier remains the impersonated one
      } else {
        // Normal behavior: set the fetched courier as the current courier
        state.courier = action.payload;
        // Also populate store locations from the courier data
        if (action.payload.storeLocations) {
          state.storeLocations = action.payload.storeLocations;
        }
      }

      // Always use the effective courier for UI elements
      const effectiveCourier = state.isImpersonating
        ? state.impersonatedCourier
        : state.courier;

      // Update document title and meta tags
      const courierName = effectiveCourier?.name || 'Courier Portal';
      const titleText = `${courierName} | Customer Portal`;
      const descriptionText = `${courierName} - Customer portal for managing your shipments and deliveries`;
      
      document.title = titleText;

      // Update meta description
      const metaDescription = document.querySelector(
        'meta[name="description"]'
      );
      if (metaDescription) {
        metaDescription.setAttribute('content', descriptionText);
      }

      // Update Open Graph meta tags
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) {
        ogTitle.setAttribute('content', titleText);
      }

      const ogDescription = document.querySelector('meta[property="og:description"]');
      if (ogDescription) {
        ogDescription.setAttribute('content', descriptionText);
      }

      const ogSiteName = document.querySelector('meta[property="og:site_name"]');
      if (ogSiteName) {
        ogSiteName.setAttribute('content', courierName);
      }

      // Update Twitter meta tags
      const twitterTitle = document.querySelector('meta[name="twitter:title"]');
      if (twitterTitle) {
        twitterTitle.setAttribute('content', titleText);
      }

      const twitterDescription = document.querySelector('meta[name="twitter:description"]');
      if (twitterDescription) {
        twitterDescription.setAttribute('content', descriptionText);
      }

      // Update URL meta tags (og:url and canonical)
      const currentUrl = window.location.href;
      
      const ogUrl = document.querySelector('meta[property="og:url"]');
      if (ogUrl) {
        ogUrl.setAttribute('content', currentUrl);
      }

      const canonicalLink = document.querySelector('link[rel="canonical"]');
      if (canonicalLink) {
        canonicalLink.setAttribute('href', currentUrl);
      }

      // Update image meta tags if courier has a logo
      if (effectiveCourier?.imageUrl) {
        const ogImage = document.querySelector('meta[property="og:image"]');
        if (ogImage) {
          ogImage.setAttribute('content', effectiveCourier.imageUrl);
        }

        const twitterImage = document.querySelector('meta[name="twitter:image"]');
        if (twitterImage) {
          twitterImage.setAttribute('content', effectiveCourier.imageUrl);
        }
      }

      injectGtm(effectiveCourier?.courierSetting?.gtmContainerId);

      // Update favicon
      const existingFavicon = document.getElementById(
        'favicon'
      ) as HTMLLinkElement;
      const faviconUrl =
        effectiveCourier?.imageUrl ||
        'https://ik.imagekit.io/47zhisylvftkf/ox_logo_only_IFYxphj5k?updatedAt=1661912145854';

      if (existingFavicon) {
        existingFavicon.href = faviconUrl;
      }

      // Inject/update a runtime web manifest based on the loaded courier
      try {
        const manifestLinkId = 'app-manifest';
        const existingManifest = document.getElementById(
          manifestLinkId
        ) as HTMLLinkElement | null;
        const manifest: any = {
          name: courierName,
          short_name: effectiveCourier?.name || 'Courier',
          description:
            effectiveCourier?.slogan ||
            'Customer portal for managing your shipments and deliveries',
          start_url: window.location.origin + '/',
          scope: window.location.origin + '/',
          display: 'standalone',
          background_color: '#ffffff',
          theme_color: '#111827',
          icons: [
            // Prefer courier logo if available; some browsers allow cross-origin icons
            effectiveCourier?.imageUrl
              ? {
                  src: effectiveCourier.imageUrl,
                  sizes: '512x512',
                  type: 'image/png',
                  purpose: 'any maskable',
                }
              : undefined,
            { src: window.location.origin + '/favicon.ico', sizes: '48x48', type: 'image/x-icon' },
          ].filter(Boolean),
        };

        const manifestBlob = new Blob([JSON.stringify(manifest)], {
          type: 'application/manifest+json',
        });
        const manifestURL = URL.createObjectURL(manifestBlob);
        if (existingManifest) {
          existingManifest.setAttribute('href', manifestURL);
        } else {
          const link = document.createElement('link');
          link.rel = 'manifest';
          link.id = manifestLinkId;
          link.href = manifestURL;
          document.head.appendChild(link);
        }
      } catch (e) {
        // noop
      }

      state.isLoading = false;
      state.error = undefined;
    });
    builder.addCase(fetchCourier.rejected, (state, action) => {
      state.courier = undefined;
      state.isLoading = false;
      state.error = action?.error?.message;
    });
    builder.addCase(fetchCourierStoreLocations.fulfilled, (state, action) => {
      state.storeLocations = action.payload;
    });
    builder.addCase(fetchCourierForImpersonation.fulfilled, (state, action) => {
      // Only start impersonation if not already impersonating and we have a current courier
      if (!state.isImpersonating && state.courier) {
        // Store the original courier for restoration later
        state.originalCourier = state.courier;
        state.impersonatedCourier = action.payload;
        state.isImpersonating = true;
        // Set the main courier to the impersonated one for seamless integration
        // This ensures all existing code that reads state.courier works correctly
        state.courier = action.payload;
      }
    });
    builder.addCase(checkPartnerLinked.fulfilled, (state, action) => {
      state.isPartnerLinked = action.payload;
    });
  },
  selectors: {
    getIsCourierLoading: (state: CourierState) => state.isLoading,
    getCourier: (state: CourierState) => state.courier,
    getCourierId: (state: CourierState) => state.courier?.id,
    getCourierName: (state: CourierState) => state.courier?.name,
    getCourierLogo: (state: CourierState) => state.courier?.imageUrl,
    getCourierRealm: (state: CourierState) => state.courier?.realm,
    getCourierSetting: (state: CourierState) => state.courier?.courierSetting,
    getCourierWarehouseLocations: (state: CourierState) =>
      state.courier?.courierSetting?.additionalShippingAddresses,
    getCourierPrefix: (state: CourierState) =>
      state.courier?.courierSetting?.prefix,
    getCourierError: (state: CourierState) => state.error,
    getHasUnpaidBills: (state: CourierState) =>
      state.courier?.courierBills?.some((bill) => bill.status === 'Unpaid'),
    getThemeSettings: (state: CourierState) => state.courier?.themeSetting,
    getWipayCredentials: (state: CourierState) =>
      state.courier?.wipayCredentials,
    getStripeCredentials: (state: CourierState) =>
      state.courier?.stripeCredentials,
    // Impersonation selectors
    getIsImpersonating: (state: CourierState) => state.isImpersonating,
    getOriginalCourier: (state: CourierState) => state.originalCourier,
    getImpersonatedCourier: (state: CourierState) => state.impersonatedCourier,
    getEffectiveCourier: (state: CourierState) =>
      state.isImpersonating ? state.impersonatedCourier : state.courier,
    getStoreLocations: (state: CourierState) => state.storeLocations,
    getIsPartnerLinked: (state: CourierState) => state.isPartnerLinked,
  },
});

export const {
  updateCourier,
  setIsLoading,
  updateStoreLocations,
  updateCourierSettings,
  updateThemeSettings,
  startImpersonation,
  stopImpersonation,
} = courierSlice.actions;

export const {
  getIsCourierLoading,
  getCourier,
  getCourierId,
  getCourierName,
  getCourierLogo,
  getCourierRealm,
  getCourierWarehouseLocations,
  getCourierPrefix,
  getCourierError,
  getHasUnpaidBills,
  getThemeSettings,
  getWipayCredentials,
  getStripeCredentials,
  getIsImpersonating,
  getOriginalCourier,
  getImpersonatedCourier,
  getEffectiveCourier,
  getStoreLocations,
  getIsPartnerLinked,
} = courierSlice.selectors;

// Memoized selectors that need to avoid creating new objects on every call
export const getCourierSetting = createSelector(
  [(state: RootState) => state.courier.courier?.courierSetting],
  (setting) => setting ? { ...setting } : {}
);

// Currency — the ISO-4217 code the courier operates in. Falls back to the
// legacy useJmd toggle (true -> JMD, else USD) so existing couriers render
// exactly as before without a data migration.
export const getCurrency = createSelector(
  [(state: RootState) => state.courier.courier?.courierSetting],
  (setting): string => setting?.currency ?? (setting?.useJmd ? 'JMD' : 'USD')
);

// Server-derived: is the courier's WhatsApp session currently usable? Missing
// (older API/SDK) is treated as connected so we never hide a working WhatsApp —
// the guard only suppresses WhatsApp prompts when it is *known* to be down.
export const getIsWhatsappConnected = createSelector(
  [(state: RootState) => state.courier.courier?.isWhatsappConnected],
  (connected) => connected !== false
);

export const getUnpaidBills = createSelector(
  [(state: RootState) => state.courier.courier?.courierBills],
  (bills) => bills?.filter((bill: any) => bill.status === 'Unpaid') || []
);

export const getIsCustomerChatEnabled = createSelector(
  [(state: RootState) => state.courier.courier?.courierSetting],
  (setting) => (setting as any)?.isCustomerChatEnabled === true
);

/** Customer reviews feature (CourierSettings.isReviewsEnabled). Gates /reviews, the More link and the post-delivery prompt. */
export const getIsReviewsEnabled = createSelector(
  [(state: RootState) => state.courier.courier?.courierSetting],
  (setting) => (setting as any)?.isReviewsEnabled === true
);

/** Tariff-code customs estimate in the Calculator (CourierSettings.isCustomsCalculatorEnabled). Estimate only. */
export const getIsCustomsCalculatorEnabled = createSelector(
  [(state: RootState) => state.courier.courier?.courierSetting],
  (setting) => (setting as any)?.isCustomsCalculatorEnabled === true
);

// Memoized impersonation selectors for consistent performance
export const getEffectiveCourierMemoized = createSelector(
  [
    (state: RootState) => state.courier.isImpersonating,
    (state: RootState) => state.courier.impersonatedCourier,
    (state: RootState) => state.courier.courier,
  ],
  (isImpersonating, impersonatedCourier, regularCourier) =>
    isImpersonating ? impersonatedCourier : regularCourier
);

export const getImpersonationStatus = createSelector(
  [
    (state: RootState) => state.courier.isImpersonating,
    (state: RootState) => state.courier.originalCourier,
    (state: RootState) => state.courier.impersonatedCourier,
  ],
  (isImpersonating, originalCourier, impersonatedCourier) => ({
    isImpersonating,
    originalCourier,
    impersonatedCourier,
  })
);

export default courierSlice.reducer;
